import { observer } from "mobx-react";
import React from "react";
import {
  Modal,
  Stack,
  Center,
  Card,
  Alert,
  Text,
  Button,
  Select,
  Loader,
  Image,
  Group,
  Checkbox,
  Divider,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconAt,
  IconAuth2fa,
  IconCurrencyDollar,
  IconHistory,
  IconSettings,
  IconTimeDuration15,
  IconRefresh,
  IconHourglass,
} from "@tabler/icons-react";

import { MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS } from "../../../helpers/constants";
import {
  BOB_MODALS,
  currencyCodes,
  currencyDisplayNames,
} from "../../helpers/constants";
import { Bob } from "../../stores/Bob";
import { CustomModalTitle } from "../details/customModalTitle";
import { unsubscribeFromPushNotifications } from "../BrowserNotifications";

// small uppercase label for the rarely-used groups below the top three
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Text fz="xs" fw={700} c="gray.5" tt="uppercase" mt="sm" mb={-6}>
    {children}
  </Text>
);

const Settings = observer(({ bob }: { bob: Bob }) => {
  const handleClose = () => {
    bob.setOpenModal(null);
  };
  const handleSetCurrencyCode = (currencyCode: string) => {
    console.log("Setting currency code to:", currencyCode);
    if (currencyCode != bob.currencyCode) {
      bob.sendDataAndDirectiveToRails("set-currency-code", {
        currencyCode: currencyCode,
      });
    }
  };

  const handleSetHistoryDays = (days: string) => {
    console.log("Setting history days to:", days);
    const daysAsNumber = parseInt(days, 10);
    if (daysAsNumber !== bob.showHistoryDays) {
      bob.sendDataAndDirectiveToRails("set-history-days", {
        showHistoryDays: daysAsNumber,
      });
    }
  };

  return (
    <Modal
      opened={bob.openModal === BOB_MODALS.SETTINGS}
      onClose={handleClose}
      // size={MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS}
      title={<CustomModalTitle text="Settings" icon={<IconSettings />} />}
    >
      <Stack gap="md">
        <Center>
          {bob.areWeWaitingOnRails && <Loader size="lg"></Loader>}
        </Center>

        {/* the three settings people actually change, most used first */}
        <Card>
          <Button
            leftSection={<IconAt size={24} />}
            onClick={() => {
              bob.setOpenModal(BOB_MODALS.EDIT_ALIAS);
            }}
          >
            Change Lightning address
          </Button>
          <Text mt="md" size="sm" c="dimmed">
            Your Lightning address ends with @rizful.com and allows anyone to
            pay you instantly.
          </Text>
        </Card>

        <Card>
          <Select
            label={
              <Group gap="xs">
                <IconHistory size={18} />
                <Text>Transaction History (days)</Text>
              </Group>
            }
            placeholder="Pick value"
            data={["1", "7", "10", "30", "60", "90", "180", "365"]}
            disabled={bob.areWeWaitingOnRails}
            defaultValue={bob.showHistoryDays.toString()}
            onChange={(value, option) => handleSetHistoryDays(value)}
          />
          <Text mt="md" size="sm" c="dimmed">
            If you want to see older transactions, you can adjust this value,
            however, this will slow down the application.
          </Text>
        </Card>

        <Card>
          <Select
            label={
              <Group gap="xs">
                <IconCurrencyDollar size={18} />
                <Text>Fiat Currency</Text>
              </Group>
            }
            placeholder="Pick value"
            data={currencyCodes.map((code) => ({
              value: code,
              label: currencyDisplayNames[code] || code.toUpperCase(),
            }))}
            disabled={bob.areWeWaitingOnRails}
            defaultValue={bob.currencyCode}
            onChange={(value, option) => handleSetCurrencyCode(value)}
          />
          <Text mt="md" size="sm" c="dimmed">
            Your transactions and spreadsheet downloads will show fiat values in
            this currency.
          </Text>
        </Card>

        {/* everything below is rarely touched */}
        <SectionTitle>Notifications</SectionTitle>
        <Card>
          <Checkbox
            label="Email me when a hold invoice payment needs my decision"
            checked={bob.workspace.accountSendEmailForHoldInvoice}
            disabled={bob.areWeWaitingOnRails}
            onChange={(event) =>
              bob.setSendEmailForHoldInvoice(event.currentTarget.checked)
            }
          />
          {(bob.workspace.accountUserWantsBrowserNotifications === true ||
            bob.workspace.accountUserWantsBrowserNotifications === false) && (
            <>
              <Divider my="md" />
              <Button
                leftSection={<IconRefresh size={24} />}
                onClick={async () => {
                  // First unsubscribe from browser push notifications
                  await unsubscribeFromPushNotifications();

                  // Then reset on the backend
                  bob.sendDataAndDirectiveToRails(
                    "reset-user-browser-notifications",
                    {},
                  );
                }}
                variant="light"
              >
                Reset Browser Notifications
              </Button>
              <Text mt="md" size="sm" c="dimmed">
                This will reset your browser notification preference and
                remove all browser subscriptions. You'll be asked again on your
                next visit.
              </Text>
            </>
          )}
        </Card>

        {/* rendered only when this account cannot create hold invoices */}
        {bob.workspace.accountHoldInvoicesRefusalReason && (
          <>
            <SectionTitle>Hold invoices</SectionTitle>
            <Card>
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <IconHourglass size={20} style={{ flexShrink: 0 }} />
                <Text size="sm" c="dimmed">
                  {bob.workspace.accountHoldInvoicesRefusalReason}
                </Text>
              </Group>
            </Card>
          </>
        )}

        <SectionTitle>Security</SectionTitle>
        <Card>
          <Button
            variant="light"
            leftSection={<IconAuth2fa size={24} />}
            onClick={() => {
              window.location.href = "/multifactor-manage";
            }}
          >
            {bob.workspace.accountIsTwoFactorProtected ? "Disable 2FA" : "Enable 2FA"}
          </Button>
          <Text mt="md" size="sm" c="dimmed">
            {bob.workspace.accountIsTwoFactorProtected
              ? "You have enabled 2FA, which adds an extra layer of security to your account. You can disable it here."
              : "We recommend enabling 2FA for added security."}
          </Text>
          {bob.workspace.accountIsTwoFactorProtected && (
            <>
              <Divider my="md" />
              <Button
                variant="light"
                leftSection={<IconTimeDuration15 size={24} />}
                onClick={() => {
                  bob.setOpenModal(BOB_MODALS.TWO_FACTOR_TIMEOUT);
                }}
              >
                Adjust 2FA Timeout
              </Button>
              <Text mt="md" size="sm" c="dimmed">
                Choose how long one 2FA code covers payments and NWC changes
                before Rizful asks for a new one.
              </Text>
            </>
          )}
        </Card>

        <SectionTitle>Nostr</SectionTitle>
        <Card>
          <Button
            variant="light"
            leftSection={
              <Image
                src="https://daxwktpz4hkwd.cloudfront.net/logos/nostr_logo_wht_lg.png"
                alt="Nostr Logo"
                w={24}
                h={24}
                fit="contain"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
            }
            onClick={() =>
              bob.setOpenModal(BOB_MODALS.SET_USER_PROVIDED_NOSTR_PUBLIC_KEY)
            }
          >
            Set Nostr Public Key
          </Button>
          <Text mt="md" size="sm" c="dimmed">
            Optionally import a Nostr public key from an external application.
          </Text>
        </Card>

        <Text mt="md" size="xs" c="dimmed">
          {bob.uuid} | {bob.workspace.email}
        </Text>
      </Stack>
    </Modal>
  );
});

export default Settings;
